import React, { useState, useMemo } from 'react';
import { Tache } from '../types';

interface CalendarViewProps {
  taches: Tache[];
  onTacheClick?: (tache: Tache) => void;
}

type ViewMode = 'day' | 'week' | 'month' | 'year';

const CalendarView: React.FC<CalendarViewProps> = ({ taches, onTacheClick }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [hoveredTache, setHoveredTache] = useState<Tache | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  // Noms des jours et mois en français
  const joursSemaine = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  const joursComplets = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
  const moisNoms = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
                   'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

  // Navigation
  const navigatePrev = () => {
    const newDate = new Date(currentDate);
    switch (viewMode) {
      case 'day':
        newDate.setDate(newDate.getDate() - 1);
        break;
      case 'week':
        newDate.setDate(newDate.getDate() - 7);
        break;
      case 'month':
        newDate.setMonth(newDate.getMonth() - 1);
        break;
      case 'year':
        newDate.setFullYear(newDate.getFullYear() - 1);
        break;
    }
    setCurrentDate(newDate);
  };

  const navigateNext = () => {
    const newDate = new Date(currentDate);
    switch (viewMode) {
      case 'day':
        newDate.setDate(newDate.getDate() + 1);
        break;
      case 'week':
        newDate.setDate(newDate.getDate() + 7);
        break;
      case 'month':
        newDate.setMonth(newDate.getMonth() + 1);
        break;
      case 'year':
        newDate.setFullYear(newDate.getFullYear() + 1);
        break;
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Titre de la période
  const getPeriodTitle = () => {
    switch (viewMode) {
      case 'day':
        return `${joursComplets[currentDate.getDay()]} ${currentDate.getDate()} ${moisNoms[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
      case 'week':
        const startOfWeek = new Date(currentDate);
        startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        return `${startOfWeek.getDate()} - ${endOfWeek.getDate()} ${moisNoms[endOfWeek.getMonth()]} ${endOfWeek.getFullYear()}`;
      case 'month':
        return `${moisNoms[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
      case 'year':
        return `${currentDate.getFullYear()}`;
    }
  };

  // Obtenir les tâches pour une date donnée
  const getTachesForDate = (date: Date) => {
    return taches.filter(tache => {
      const debut = new Date(tache.dateDebut);
      const fin = new Date(tache.dateFin);
      const checkDate = new Date(date);
      checkDate.setHours(0, 0, 0, 0);
      debut.setHours(0, 0, 0, 0);
      fin.setHours(0, 0, 0, 0);
      return checkDate >= debut && checkDate <= fin;
    });
  };

  // Obtenir la couleur selon l'état
  const getTaskColor = (tache: Tache) => {
    switch (tache.etat) {
      case 'terminee': return 'bg-green-500';
      case 'en_cours': return 'bg-blue-500';
      case 'en_attente': return 'bg-amber-500';
      default: return 'bg-gray-400';
    }
  };

  const getTaskBorderColor = (tache: Tache) => {
    switch (tache.etat) {
      case 'terminee': return 'border-green-600';
      case 'en_cours': return 'border-blue-600';
      case 'en_attente': return 'border-amber-600';
      default: return 'border-gray-500';
    }
  };

  // Générer les jours du mois
  const getMonthDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days: (Date | null)[] = [];

    // Jours du mois précédent pour remplir la première semaine
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }

    // Jours du mois actuel
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  // Générer les jours de la semaine
  const getWeekDays = () => {
    const days: Date[] = [];
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push(day);
    }
    
    return days;
  };

  // Générer les mois de l'année
  const getYearMonths = () => {
    const months: Date[] = [];
    const year = currentDate.getFullYear();
    
    for (let i = 0; i < 12; i++) {
      months.push(new Date(year, i, 1));
    }
    
    return months;
  };

  // Obtenir les tâches pour un mois donné
  const getTachesForMonth = (monthDate: Date) => {
    return taches.filter(tache => {
      const debut = new Date(tache.dateDebut);
      const fin = new Date(tache.dateFin);
      const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
      const monthEnd = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
      return (debut <= monthEnd && fin >= monthStart);
    });
  };

  // Gestion du tooltip
  const handleTacheHover = (e: React.MouseEvent, tache: Tache) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    });
    setHoveredTache(tache);
  };

  // Formater la date
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  // Traduire l'état
  const translateEtat = (etat: string) => {
    switch (etat) {
      case 'terminee': return 'Terminée';
      case 'en_cours': return 'En cours';
      case 'en_attente': return 'En attente';
      default: return etat;
    }
  };

  // Traduire la complexité
  const translateComplexite = (complexite: string) => {
    switch (complexite) {
      case 'facile': return 'Facile';
      case 'moyen': return 'Moyen';
      case 'difficile': return 'Difficile';
      default: return complexite;
    }
  };

  // Vérifier si c'est aujourd'hui
  const isToday = (date: Date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  // Rendu de la vue jour
  const renderDayView = () => {
    const dayTaches = getTachesForDate(currentDate);
    const hours = Array.from({ length: 24 }, (_, i) => i);

    return (
      <div className="border rounded-lg overflow-hidden">
        <div className="grid grid-cols-1 divide-y">
          {hours.map(hour => (
            <div key={hour} className="flex min-h-16">
              <div className="w-16 p-2 text-xs text-gray-500 bg-gray-50 border-r">
                {hour.toString().padStart(2, '0')}:00
              </div>
              <div className="flex-1 p-1 relative">
                {hour === 9 && dayTaches.map(tache => (
                  <div
                    key={tache.id}
                    className={`${getTaskColor(tache)} text-white text-xs p-2 rounded mb-1 cursor-pointer
                      hover:brightness-110 transition-all`}
                    onMouseEnter={(e) => handleTacheHover(e, tache)}
                    onMouseLeave={() => setHoveredTache(null)}
                    onClick={() => onTacheClick?.(tache)}
                  >
                    <div className="font-medium truncate">{tache.nom}</div>
                    <div className="text-white/80">{translateEtat(tache.etat)}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Rendu de la vue semaine
  const renderWeekView = () => {
    const weekDays = getWeekDays();
    const hours = Array.from({ length: 12 }, (_, i) => i + 8); // 8h à 19h

    return (
      <div className="border rounded-lg overflow-hidden">
        {/* En-tête des jours */}
        <div className="grid grid-cols-8 border-b bg-gray-50">
          <div className="p-2 border-r"></div>
          {weekDays.map((day, idx) => (
            <div
              key={idx}
              className={`p-2 text-center border-r ${isToday(day) ? 'bg-green-50' : ''}`}
            >
              <div className="text-xs text-gray-500">{joursSemaine[day.getDay()]}</div>
              <div className={`text-lg font-semibold ${isToday(day) ? 'text-green-600' : 'text-gray-800'}`}>
                {day.getDate()}
              </div>
            </div>
          ))}
        </div>

        {/* Grille horaire */}
        <div className="max-h-96 overflow-y-auto">
          {hours.map(hour => (
            <div key={hour} className="grid grid-cols-8 border-b min-h-12">
              <div className="p-1 text-xs text-gray-500 bg-gray-50 border-r text-right pr-2">
                {hour}:00
              </div>
              {weekDays.map((day, idx) => {
                const dayTaches = getTachesForDate(day);
                return (
                  <div
                    key={idx}
                    className={`p-1 border-r relative ${isToday(day) ? 'bg-green-50/30' : ''}`}
                  >
                    {hour === 9 && dayTaches.slice(0, 2).map(tache => (
                      <div
                        key={tache.id}
                        className={`${getTaskColor(tache)} text-white text-xs p-1 rounded mb-1 cursor-pointer
                          hover:brightness-110 transition-all truncate`}
                        onMouseEnter={(e) => handleTacheHover(e, tache)}
                        onMouseLeave={() => setHoveredTache(null)}
                        onClick={() => onTacheClick?.(tache)}
                      >
                        {tache.nom}
                      </div>
                    ))}
                    {hour === 9 && dayTaches.length > 2 && (
                      <div className="text-xs text-gray-500">
                        +{dayTaches.length - 2} autres
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Rendu de la vue mois
  const renderMonthView = () => {
    const monthDays = getMonthDays();

    return (
      <div className="border rounded-lg overflow-hidden">
        {/* En-tête des jours de la semaine */}
        <div className="grid grid-cols-7 bg-gray-50 border-b">
          {joursSemaine.map(jour => (
            <div key={jour} className="p-2 text-center text-sm font-medium text-gray-600 border-r last:border-r-0">
              {jour}
            </div>
          ))}
        </div>

        {/* Grille des jours */}
        <div className="grid grid-cols-7">
          {monthDays.map((day, idx) => {
            const dayTaches = day ? getTachesForDate(day) : [];
            const today = day && isToday(day);

            return (
              <div
                key={idx}
                className={`min-h-24 p-1 border-r border-b last:border-r-0 
                  ${!day ? 'bg-gray-50' : ''} 
                  ${today ? 'bg-green-50' : ''}`}
              >
                {day && (
                  <>
                    <div className={`text-sm font-medium mb-1 ${today ? 'text-green-600' : 'text-gray-700'}`}>
                      {day.getDate()}
                    </div>
                    <div className="space-y-1">
                      {dayTaches.slice(0, 3).map(tache => (
                        <div
                          key={tache.id}
                          className={`${getTaskColor(tache)} text-white text-xs px-1.5 py-0.5 rounded cursor-pointer
                            hover:brightness-110 transition-all truncate`}
                          onMouseEnter={(e) => handleTacheHover(e, tache)}
                          onMouseLeave={() => setHoveredTache(null)}
                          onClick={() => onTacheClick?.(tache)}
                        >
                          {tache.nom}
                        </div>
                      ))}
                      {dayTaches.length > 3 && (
                        <div className="text-xs text-gray-500 pl-1">
                          +{dayTaches.length - 3} autres
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Rendu de la vue année
  const renderYearView = () => {
    const yearMonths = getYearMonths();

    return (
      <div className="grid grid-cols-3 md:grid-cols-4 gap-4">
        {yearMonths.map((monthDate, idx) => {
          const monthTaches = getTachesForMonth(monthDate);
          const isCurrentMonth = monthDate.getMonth() === new Date().getMonth() &&
                                 monthDate.getFullYear() === new Date().getFullYear();

          return (
            <div
              key={idx}
              className={`border rounded-lg p-3 ${isCurrentMonth ? 'border-green-500 bg-green-50' : ''}`}
            >
              <div className={`font-semibold mb-2 ${isCurrentMonth ? 'text-green-700' : 'text-gray-800'}`}>
                {moisNoms[monthDate.getMonth()]}
              </div>
              
              {monthTaches.length === 0 ? (
                <div className="text-xs text-gray-400">Aucune tâche</div>
              ) : (
                <div className="space-y-1">
                  {monthTaches.slice(0, 4).map(tache => (
                    <div
                      key={tache.id}
                      className={`text-xs px-2 py-1 rounded cursor-pointer border-l-2 bg-white
                        hover:bg-gray-50 transition-all ${getTaskBorderColor(tache)}`}
                      onMouseEnter={(e) => handleTacheHover(e, tache)}
                      onMouseLeave={() => setHoveredTache(null)}
                      onClick={() => onTacheClick?.(tache)}
                    >
                      <div className="font-medium truncate text-gray-800">{tache.nom}</div>
                    </div>
                  ))}
                  {monthTaches.length > 4 && (
                    <div className="text-xs text-gray-500">
                      +{monthTaches.length - 4} autres tâches
                    </div>
                  )}
                </div>
              )}
              
              <div className="mt-2 text-xs text-gray-500">
                {monthTaches.length} tâche{monthTaches.length !== 1 ? 's' : ''}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Header avec contrôles */}
      <div className="flex items-center justify-between p-4 border-b bg-gray-50">
        <h3 className="text-lg font-semibold text-gray-800">Calendrier</h3>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600 mr-2">Vue:</span>
          <div className="flex rounded-lg overflow-hidden border border-gray-300">
            {(['day', 'week', 'month', 'year'] as ViewMode[]).map((mode) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  viewMode === mode
                    ? 'bg-green-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {mode === 'day' ? 'Jour' : mode === 'week' ? 'Semaine' : mode === 'month' ? 'Mois' : 'Année'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between p-4 border-b">
        <button
          onClick={navigatePrev}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        
        <div className="flex items-center gap-4">
          <h4 className="text-lg font-semibold text-gray-800">{getPeriodTitle()}</h4>
          <button
            onClick={goToToday}
            className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
          >
            Aujourd'hui
          </button>
        </div>
        
        <button
          onClick={navigateNext}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Légende */}
      <div className="flex items-center gap-4 px-4 py-2 bg-gray-50 border-b text-sm">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-green-500"></div>
          <span className="text-gray-600">Terminée</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-blue-500"></div>
          <span className="text-gray-600">En cours</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-amber-500"></div>
          <span className="text-gray-600">En attente</span>
        </div>
      </div>

      {/* Contenu du calendrier */}
      <div className="p-4">
        {viewMode === 'day' && renderDayView()}
        {viewMode === 'week' && renderWeekView()}
        {viewMode === 'month' && renderMonthView()}
        {viewMode === 'year' && renderYearView()}
      </div>

      {/* Tooltip */}
      {hoveredTache && (
        <div
          className="fixed z-50 bg-white rounded-lg shadow-xl border border-gray-200 p-4 max-w-sm pointer-events-none"
          style={{
            left: `${Math.min(tooltipPosition.x, window.innerWidth - 320)}px`,
            top: `${tooltipPosition.y}px`,
            transform: 'translate(-50%, -100%)'
          }}
        >
          <div className="font-semibold text-gray-900 mb-2">{hoveredTache.nom}</div>
          
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium text-white ${getTaskColor(hoveredTache)}`}>
                {translateEtat(hoveredTache.etat)}
              </span>
              <span className="text-gray-500">•</span>
              <span className="text-gray-600">{translateComplexite(hoveredTache.complexite)}</span>
            </div>
            
            <div className="text-gray-600">
              <div className="flex justify-between">
                <span>Début:</span>
                <span className="font-medium">{formatDate(hoveredTache.dateDebut)}</span>
              </div>
              <div className="flex justify-between">
                <span>Fin:</span>
                <span className="font-medium">{formatDate(hoveredTache.dateFin)}</span>
              </div>
            </div>
            
            {hoveredTache.description && (
              <div className="pt-2 border-t border-gray-100">
                <div className="text-gray-500 text-xs mb-1">Description:</div>
                <div className="text-gray-700">{hoveredTache.description}</div>
              </div>
            )}
            
            {hoveredTache.livrables && (
              <div className="pt-2 border-t border-gray-100">
                <div className="text-gray-500 text-xs mb-1">Livrables:</div>
                <div className="text-gray-700">{hoveredTache.livrables}</div>
              </div>
            )}
            
            {hoveredTache.enRetard && (
              <div className="pt-2 border-t border-gray-100">
                <div className="flex items-center gap-1 text-red-600">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">Tâche en retard</span>
                </div>
              </div>
            )}
          </div>
          
          {/* Flèche du tooltip */}
          <div className="absolute left-1/2 bottom-0 transform -translate-x-1/2 translate-y-full">
            <div className="w-3 h-3 bg-white border-r border-b border-gray-200 transform rotate-45 -mt-1.5"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarView;
